<template>
  <li
    ref="row"
    class="task-row"
    :class="rowClasses"
    :style="rowStyle"
    :data-task-id="task.id"
    @dragenter.prevent="handleDragEnter"
    @dragover.prevent="handleDragOver"
    @drop.prevent="handleDrop"
  >
    <div v-if="isMobile" class="swipe-underlay swipe-underlay-left" :class="{ visible: currentOpen === 'drag' || effectiveOffset > 0 }">
      <button
        class="mobile-reveal-btn mobile-drag-btn"
        :class="{ loading: busy }"
        type="button"
        :draggable="draggable"
        :disabled="!draggable || busy"
        aria-label="Mover tarea"
        title="Arrastrar para reordenar"
        @mousedown.stop
        @touchstart.prevent.stop="handleHandleTouchStart"
        @touchmove.prevent.stop="handleHandleTouchMove"
        @touchend.prevent.stop="handleHandleTouchEnd"
        @touchcancel.prevent.stop="handleHandleTouchEnd"
        @dragstart="handleDragStart"
        @dragend="handleDragEnd"
      >
        <span v-if="busy" class="underlay-spinner" aria-hidden="true"></span>
        <span v-else class="drag-dots">⋮⋮</span>
      </button>
    </div>

    <div v-if="isMobile" class="swipe-underlay swipe-underlay-right" :class="{ visible: currentOpen === 'actions' || effectiveOffset < 0 }">
      <div class="mobile-action-stack">
        <label
          v-if="allowImage"
          class="mobile-reveal-btn mobile-image-btn"
          :class="{ disabled: disableUnsafeActions, loading: busy }"
          :title="task.image_url ? 'Reemplazar imagen' : 'Agregar imagen'"
        >
          <input
            type="file"
            class="file-input"
            accept=".png,.jpg,.jpeg,.svg,.heic,.heif,image/png,image/jpeg,image/svg+xml,image/heic,image/heif"
            :disabled="disableUnsafeActions"
            @change="handleFileChange"
          >
          <span v-if="busy" class="underlay-spinner" aria-hidden="true"></span>
          <template v-else>
            <span class="mobile-action-copy">🖼</span>
            <small>{{ task.image_url ? 'Foto' : 'Foto' }}</small>
          </template>
        </label>

        <button
          class="mobile-reveal-btn mobile-delete-btn"
          :class="{ loading: busy }"
          type="button"
          aria-label="Eliminar tarea"
          title="Eliminar tarea"
          @click="handleDeleteClick"
          :disabled="busy"
        >
          <span v-if="busy" class="underlay-spinner light" aria-hidden="true"></span>
          <template v-else>
            <svg viewBox="0 0 24 24" aria-hidden="true" class="trash-icon">
              <path d="M9 3h6l1 2h4v2H4V5h4l1-2Zm-2 6h2v9H7V9Zm4 0h2v9h-2V9Zm4 0h2v9h-2V9ZM6 7h12l-1 13a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L6 7Z" fill="currentColor"/>
            </svg>
            <small>Borrar</small>
          </template>
        </button>
      </div>
    </div>

    <div
      class="task-card"
      :class="{ 'card-open-left': currentOpen === 'drag', 'card-open-right': currentOpen === 'actions' }"
      :style="swipeCardStyle"
      @touchstart.passive="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
      @touchcancel="handleTouchCancel"
    >
      <div class="task-main">
        <button
          v-if="!isMobile"
          class="drag-handle desktop-handle"
          type="button"
          :draggable="draggable"
          :disabled="!draggable || busy"
          aria-label="Mover tarea"
          title="Arrastrar para reordenar"
          @mousedown.stop
          @touchstart.stop
          @dragstart="handleDragStart"
          @dragend="handleDragEnd"
        >
          ⋮⋮
        </button>

        <button class="toggle-btn" @click="$emit('toggle', task.id)" :aria-label="isDone ? 'Marcar pendiente' : 'Marcar completada'">
          <span class="check-mark"></span>
        </button>

        <div class="task-content">
          <textarea
            ref="editor"
            v-model="draft"
            class="task-input"
            rows="1"
            placeholder="Escribe una tarea"
            @input="resize"
            @blur="commit"
            @keydown.enter.exact.prevent="commitAndStay"
          ></textarea>

          <div v-if="task.image_url" class="task-image-card">
            <a class="task-image-link" :href="task.image_url" target="_blank" rel="noreferrer">
              <img :src="task.image_url" alt="Imagen de la tarea" class="task-image">
            </a>

            <div class="task-image-meta">
              <span>Imagen adjunta</span>
              <small v-if="task.image_width && task.image_height">{{ task.image_width }} × {{ task.image_height }} px</small>
            </div>
          </div>
        </div>
      </div>

      <div class="task-actions">
        <label
          v-if="allowImage && !isMobile"
          class="icon-btn upload-btn"
          :class="{ disabled: disableUnsafeActions }"
          :title="task.image_url ? 'Reemplazar imagen' : 'Agregar imagen'"
        >
          <input
            type="file"
            class="file-input"
            accept=".png,.jpg,.jpeg,.svg,.heic,.heif,image/png,image/jpeg,image/svg+xml,image/heic,image/heif"
            :disabled="disableUnsafeActions"
            @change="handleFileChange"
          >
          <span>🖼</span>
        </label>

        <button
          v-if="allowImage && task.image_url && !isMobile"
          class="icon-btn subtle image-remove-btn"
          @click="$emit('remove-image', task.id)"
          :disabled="disableUnsafeActions"
          aria-label="Eliminar imagen"
          title="Quitar imagen"
        >
          −
        </button>

        <button
          v-if="!isMobile"
          class="icon-btn danger delete-btn"
          @click="handleDeleteClick" :disabled="busy"
          aria-label="Eliminar tarea"
          title="Eliminar tarea"
        >
          ×
        </button>
      </div>
    </div>
  </li>
</template>

<script>
export default {
  name: 'TaskItem',
  props: {
    task: { type: Object, required: true },
    accentColor: { type: String, default: '#F5D76E' },
    allowImage: { type: Boolean, default: false },
    uploadDisabled: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
    draggable: { type: Boolean, default: true },
    isDragging: { type: Boolean, default: false },
    dropTarget: { type: Boolean, default: false },
  },
  emits: [
    'toggle', 'rename', 'delete', 'upload-image', 'remove-image',
    'drag-start', 'drag-end', 'drag-enter', 'drop-task',
    'touch-drag-start', 'touch-drag-move', 'touch-drag-drop', 'touch-drag-end',
  ],
  data() {
    return {
      draft: this.task.body,
      isMobile: window.innerWidth <= 820,
      currentOpen: '',
      touchStartX: 0,
      touchStartY: 0,
      startOffset: 0,
      currentOffset: 0,
      swiping: false,
      swipeLocked: false,
      swipeIgnored: false,
      swipeAxis: '',
      actionRevealWidth: 142,
      touchDraggingFromHandle: false,
      touchDragTargetId: '',
      touchDragStartPoint: null,
      touchDragTranslateX: 0,
      touchDragTranslateY: 0,
      desktopDragImageEl: null,
      activeTouchId: null,
    };
  },
  computed: {
    isDone() {
      return Number(this.task?.completed || 0) === 1;
    },
    effectiveOffset() {
      if (!this.isMobile) return 0;
      if (this.swiping) return this.currentOffset;
      if (this.currentOpen === 'drag') return this.actionRevealWidth;
      if (this.currentOpen === 'actions') return -this.actionRevealWidth;
      return 0;
    },
    disableUnsafeActions() {
      return this.uploadDisabled || this.busy;
    },
    rowStyle() {
      return {
        '--task-accent-soft': this.hexToRgba(this.accentColor, 0.2),
        '--swipe-offset': `${this.effectiveOffset}px`,
      };
    },
    swipeCardStyle() {
      const translateX = this.effectiveOffset + (this.touchDraggingFromHandle ? this.touchDragTranslateX : 0);
      const translateY = (this.touchDraggingFromHandle ? this.touchDragTranslateY : 0);
      const rotate = this.touchDraggingFromHandle ? Math.max(-2.8, Math.min(2.8, this.touchDragTranslateX / 18)) : 0;
      const scale = this.touchDraggingFromHandle ? 1.035 : 1;
      const transform = `translate3d(${translateX}px, ${translateY}px, 0) scale(${scale}) rotate(${rotate}deg)`;
      return {
        transform,
        transition: this.swiping || this.touchDraggingFromHandle ? 'none' : 'transform .22s ease, box-shadow .18s ease, background .18s ease, border-color .18s ease, border-radius .18s ease',
        zIndex: this.touchDraggingFromHandle ? '14' : undefined,
        touchAction: this.touchDraggingFromHandle ? 'none' : undefined,
      };
    },
    rowClasses() {
      return {
        done: this.isDone,
        dragging: this.isDragging || this.touchDraggingFromHandle,
        'drop-target': !!this.dropTarget,
        mobile: this.isMobile,
        'mobile-actions-revealed': this.isMobile && this.currentOpen === 'actions',
        'mobile-drag-revealed': this.isMobile && this.currentOpen === 'drag',
        'touch-reordering': this.touchDraggingFromHandle,
      };
    },
  },
  watch: {
    'task.body'(value) {
      if (value !== this.draft) {
        this.draft = value;
        this.$nextTick(() => this.resize());
      }
    },
  },
  mounted() {
    this.resize();
    window.addEventListener('resize', this.handleResize);
    document.addEventListener('click', this.handleDocumentPointer, true);
    document.addEventListener('touchstart', this.handleDocumentPointer, true);
    window.addEventListener('task-item-open', this.handleExternalOpen);
    window.addEventListener('blur', this.handleWindowBlur);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('click', this.handleDocumentPointer, true);
    document.removeEventListener('touchstart', this.handleDocumentPointer, true);
    window.removeEventListener('task-item-open', this.handleExternalOpen);
    window.removeEventListener('blur', this.handleWindowBlur);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.unregisterGlobalTouchDragListeners();
    this.destroyDesktopDragImage();
    document.body.style.overflow = '';
  },
  methods: {
    hexToRgba(value, alpha) {
      const clean = (value || '').replace('#', '');
      if (clean.length !== 6) return `rgba(245, 215, 110, ${alpha})`;
      const bigint = Number.parseInt(clean, 16);
      const red = (bigint >> 16) & 255;
      const green = (bigint >> 8) & 255;
      const blue = bigint & 255;
      return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
    },
    handleResize() {
      this.isMobile = window.innerWidth <= 820;
      if (!this.isMobile) {
        this.closeSwipe();
      }
    },
    resize() {
      const element = this.$refs.editor;
      if (!element) return;
      element.style.height = 'auto';
      element.style.height = `${element.scrollHeight}px`;
    },
    commit() {
      const value = this.draft.trim();
      if (!value || value === this.task.body) {
        this.draft = this.task.body;
        this.$nextTick(() => this.resize());
        return;
      }
      this.$emit('rename', { taskId: this.task.id, body: value });
    },
    commitAndStay() {
      this.commit();
      this.$refs.editor?.focus?.();
    },
    handleFileChange(event) {
      if (this.disableUnsafeActions) return;
      const file = event.target.files?.[0];
      if (!file) return;
      this.closeSwipe();
      this.$emit('upload-image', { taskId: this.task.id, file });
      event.target.value = '';
    },
    handleDeleteClick() {
      if (this.busy) return;
      this.closeSwipe();
      this.$emit('delete', this.task.id);
    },
    createDesktopDragImage(event) {
      const card = this.$refs.row?.querySelector?.('.task-card');
      if (!card || !event?.dataTransfer) return;
      const clone = card.cloneNode(true);
      clone.style.position = 'fixed';
      clone.style.top = '-9999px';
      clone.style.left = '-9999px';
      clone.style.width = `${card.getBoundingClientRect().width}px`;
      clone.style.pointerEvents = 'none';
      clone.style.margin = '0';
      clone.style.transform = 'translate3d(0, 0, 0) scale(1.02)';
      clone.style.background = 'rgba(255,255,255,.98)';
      clone.style.border = '1px solid rgba(148,163,184,.18)';
      clone.style.borderRadius = '22px';
      clone.style.boxShadow = '0 18px 34px rgba(15,23,42,.14), 0 6px 16px rgba(15,23,42,.08)';
      clone.style.zIndex = '9999';
      document.body.appendChild(clone);
      this.desktopDragImageEl = clone;
      const rect = card.getBoundingClientRect();
      const offsetX = Math.max(24, Math.min(rect.width - 24, event.clientX - rect.left));
      const offsetY = Math.max(18, Math.min(rect.height - 18, event.clientY - rect.top));
      event.dataTransfer.setDragImage(clone, offsetX, offsetY);
    },
    destroyDesktopDragImage() {
      if (this.desktopDragImageEl?.parentNode) {
        this.desktopDragImageEl.parentNode.removeChild(this.desktopDragImageEl);
      }
      this.desktopDragImageEl = null;
    },
    handleDragStart(event) {
      if (!this.draggable) {
        event.preventDefault();
        return;
      }
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', this.task.id);
      this.createDesktopDragImage(event);
      this.$emit('drag-start', this.task.id);
    },
    handleDragEnd() {
      this.destroyDesktopDragImage();
      this.$emit('drag-end');
      if (this.isMobile) {
        this.closeSwipe();
      }
    },
    handleDragEnter() {
      this.$emit('drag-enter', { taskId: this.task.id });
    },
    handleDragOver() {
      this.$emit('drag-enter', { taskId: this.task.id });
    },
    handleDrop() {
      this.$emit('drop-task', { taskId: this.task.id });
      if (this.isMobile) {
        this.closeSwipe();
      }
    },
    handleHandleTouchStart(event) {
      if (!this.isMobile || !this.draggable || this.busy) return;
      const touch = event.changedTouches?.[0];
      if (!touch) return;
      this.resetTouchDragState({ silent: true });
      this.touchDraggingFromHandle = true;
      this.activeTouchId = touch.identifier;
      this.touchDragTargetId = this.task.id;
      this.touchDragStartPoint = { x: touch.clientX, y: touch.clientY };
      this.touchDragTranslateX = 0;
      this.touchDragTranslateY = 0;
      document.body.style.overflow = 'hidden';
      this.registerGlobalTouchDragListeners();
      this.$emit('touch-drag-start', this.task.id);
    },
    handleHandleTouchMove(event) {
      if (!this.isMobile || !this.touchDraggingFromHandle) return;
      const touch = this.getTrackedTouch(event);
      if (!touch) return;
      if (this.touchDragStartPoint) {
        this.touchDragTranslateX = (touch.clientX - this.touchDragStartPoint.x) * 0.22;
        this.touchDragTranslateY = touch.clientY - this.touchDragStartPoint.y;
      }
      const rowElement = this.$refs.row;
      let row = null;
      if (rowElement) {
        const previousPointerEvents = rowElement.style.pointerEvents;
        rowElement.style.pointerEvents = 'none';
        row = document.elementFromPoint(touch.clientX, touch.clientY)?.closest?.('[data-task-id]') || null;
        rowElement.style.pointerEvents = previousPointerEvents;
      } else {
        row = document.elementFromPoint(touch.clientX, touch.clientY)?.closest?.('[data-task-id]') || null;
      }
      const targetTaskId = row?.getAttribute?.('data-task-id') || this.touchDragTargetId || this.task.id;
      this.touchDragTargetId = targetTaskId;
      this.$emit('touch-drag-move', { taskId: this.task.id, targetTaskId, clientX: touch.clientX, clientY: touch.clientY });
      if (event.cancelable) {
        event.preventDefault();
      }
    },
    handleHandleTouchEnd(event) {
      if (!this.isMobile || !this.touchDraggingFromHandle) return;
      const shouldDrop = !event || !!this.getTrackedTouch(event) || (event.type !== 'touchcancel');
      if (shouldDrop) {
        this.$emit('touch-drag-drop', { taskId: this.task.id, targetTaskId: this.touchDragTargetId || this.task.id });
      }
      this.resetTouchDragState();
    },
    getTrackedTouch(event) {
      const touches = [
        ...(event?.changedTouches ? Array.from(event.changedTouches) : []),
        ...(event?.touches ? Array.from(event.touches) : []),
      ];
      if (this.activeTouchId === null) {
        return touches[0] || null;
      }
      return touches.find((item) => item.identifier === this.activeTouchId) || null;
    },
    registerGlobalTouchDragListeners() {
      window.addEventListener('touchmove', this.handleHandleTouchMove, { passive: false });
      window.addEventListener('touchend', this.handleHandleTouchEnd, { passive: false });
      window.addEventListener('touchcancel', this.handleHandleTouchEnd, { passive: false });
    },
    unregisterGlobalTouchDragListeners() {
      window.removeEventListener('touchmove', this.handleHandleTouchMove, { passive: false });
      window.removeEventListener('touchend', this.handleHandleTouchEnd, { passive: false });
      window.removeEventListener('touchcancel', this.handleHandleTouchEnd, { passive: false });
    },
    resetTouchDragState(options = {}) {
      const { silent = false } = options;
      const wasDragging = this.touchDraggingFromHandle;
      this.touchDraggingFromHandle = false;
      this.activeTouchId = null;
      this.touchDragTargetId = '';
      this.touchDragStartPoint = null;
      this.touchDragTranslateX = 0;
      this.touchDragTranslateY = 0;
      document.body.style.overflow = '';
      this.unregisterGlobalTouchDragListeners();
      if (wasDragging && !silent) {
        this.$emit('touch-drag-end', { taskId: this.task.id });
      }
      this.closeSwipe();
    },
    handleWindowBlur() {
      if (!this.touchDraggingFromHandle) return;
      this.resetTouchDragState();
    },
    handleVisibilityChange() {
      if (document.hidden && this.touchDraggingFromHandle) {
        this.resetTouchDragState();
      }
    },
    shouldIgnoreSwipe(target) {
      if (!target?.closest) return false;

      if (target.closest('.mobile-reveal-btn,.task-image-link,.toggle-btn,.icon-btn,button[type=button]')) {
        return true;
      }

      const formField = target.closest('input:not([type=file]),select');
      if (formField) {
        return true;
      }

      const textarea = target.closest('textarea');
      if (textarea && document.activeElement === textarea) {
        return true;
      }

      return false;
    },
    handleTouchStart(event) {
      if (!this.isMobile || this.touchDraggingFromHandle) return;
      const touch = event.changedTouches?.[0];
      if (!touch) return;
      this.swipeIgnored = this.shouldIgnoreSwipe(event.target);
      if (this.swipeIgnored) return;
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
      this.startOffset = this.effectiveOffset;
      this.currentOffset = this.effectiveOffset;
      this.swipeAxis = '';
      this.swiping = true;
      this.swipeLocked = false;
    },
    handleTouchMove(event) {
      if (!this.isMobile || !this.swiping || this.swipeIgnored || this.touchDraggingFromHandle) return;
      const touch = event.changedTouches?.[0];
      if (!touch) return;
      const deltaX = touch.clientX - this.touchStartX;
      const deltaY = touch.clientY - this.touchStartY;

      if (!this.swipeLocked) {
        if (Math.abs(deltaY) > 18 && Math.abs(deltaY) > Math.abs(deltaX)) {
          this.swiping = false;
          return;
        }
        if (Math.abs(deltaX) < 12 || Math.abs(deltaX) < Math.abs(deltaY)) {
          return;
        }
        this.swipeLocked = true;
        this.swipeAxis = 'x';
      }

      const next = Math.max(-this.actionRevealWidth, Math.min(this.actionRevealWidth, this.startOffset + deltaX));
      this.currentOffset = next;

      if (event.cancelable) {
        event.preventDefault();
      }
    },
    handleTouchEnd() {
      if (!this.isMobile || this.touchDraggingFromHandle) return;
      if (!this.swipeIgnored) {
        const threshold = this.actionRevealWidth * 0.45;
        if (this.currentOffset >= threshold) {
          this.openSwipe('drag');
        } else if (this.currentOffset <= -threshold) {
          this.openSwipe('actions');
        } else {
          this.closeSwipe();
        }
      }
      this.swiping = false;
      this.swipeLocked = false;
      this.swipeIgnored = false;
      this.swipeAxis = '';
    },
    handleTouchCancel() {
      this.swiping = false;
      this.swipeLocked = false;
      this.swipeIgnored = false;
      this.swipeAxis = '';
      this.currentOffset = 0;
      if (this.touchDraggingFromHandle) {
        this.resetTouchDragState();
      } else {
        this.closeSwipe();
      }
    },
    openSwipe(state) {
      this.currentOpen = state;
      this.currentOffset = state === 'drag' ? this.actionRevealWidth : (state === 'delete' ? -this.actionRevealWidth : 0);
      window.dispatchEvent(new CustomEvent('task-item-open', { detail: { taskId: this.task.id } }));
    },
    closeSwipe() {
      this.currentOpen = '';
      this.currentOffset = 0;
    },
    handleDocumentPointer(event) {
      if (!this.isMobile || !this.currentOpen || this.touchDraggingFromHandle) return;
      if (this.$refs.row?.contains?.(event.target)) return;
      this.closeSwipe();
    },
    handleExternalOpen(event) {
      const taskId = event?.detail?.taskId;
      if (!taskId || taskId === this.task.id) return;
      this.closeSwipe();
    },
  },
};
</script>

<style scoped>
.task-row {
  position: relative;
  min-height: 74px;
  border-bottom: 1px dashed rgba(148, 163, 184, 0.35);
  transition: opacity .16s ease, box-shadow .16s ease, background .16s ease, transform .16s ease;
  overflow: hidden;
}
.task-row.dragging,
.task-row.touch-reordering {
  overflow: visible;
  z-index: 6;
}
.task-row.dragging { opacity: 1; }
.task-row.drop-target { background: rgba(239,246,255,.92); box-shadow: inset 0 0 0 1px rgba(59,130,246,.16); transform: translateY(-1px); }
.task-row.drop-target::after { content: ''; position: absolute; left: 0; right: 0; bottom: -2px; height: 3px; border-radius: 999px; background: #2563eb; z-index: 1; }

.task-card {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 4px 14px;
  min-height: 74px;
  background: transparent;
  will-change: transform;
  transition: transform .18s ease, box-shadow .18s ease, background .18s ease, border-color .18s ease, border-radius .18s ease;
}
.task-row.dragging .task-card,
.task-row.touch-reordering .task-card {
  background: rgba(255,255,255,.99);
  border: 1px solid rgba(148,163,184,.18);
  border-radius: 22px;
  box-shadow: 0 20px 38px rgba(15,23,42,.16), 0 8px 18px rgba(15,23,42,.10);
}
.task-row.dragging .task-card {
  transform: translateY(-4px) scale(1.01);
}
.task-row.touch-reordering .task-card {
  transform-origin: center center;
  filter: saturate(1.04);
}

.swipe-underlay {
  position: absolute;
  top: 8px;
  bottom: 8px;
  width: 152px;
  display: flex;
  align-items: stretch;
  z-index: 0;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity .18s ease, visibility .18s ease;
}
.swipe-underlay.visible {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
}
.swipe-underlay-left { left: 0; justify-content: flex-start; }
.swipe-underlay-right { right: 0; justify-content: flex-end; }
.mobile-reveal-btn {
  width: 100%;
  border: none;
  border-radius: 18px;
  display: grid;
  place-items: center;
  align-content: center;
  gap: 4px;
  cursor: pointer;
  -webkit-appearance: none;
  appearance: none;
}
.mobile-reveal-btn.loading {
  pointer-events: none;
}
.mobile-drag-btn {
  background: rgba(255,255,255,.94);
  /* border: 1px solid rgba(148, 163, 184, 0.22); */
  box-shadow: 0 0 15px rgba(155,155,155, 0.3);
  color: #64748b;
  touch-action: none;
  border-radius: 18px 0 0 18px;
}
.drag-dots {
  font-size: 24px;
  line-height: 1;
  letter-spacing: -2px;
}
.mobile-action-stack {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  justify-content: end;
}
.mobile-delete-btn {
  background: #dc2626;
  color: #fff;
  border-radius: 18px;
  box-shadow: 0 10px 22px rgba(220,38,38,.22);
}
.mobile-image-btn {
  background: rgba(17,24,39,.96);
  color: #fff;
  border-radius: 18px;
  box-shadow: 0 10px 22px rgba(15,23,42,.18);
}
.mobile-image-btn.disabled {
  opacity: .55;
  pointer-events: none;
}
.mobile-action-copy {
  font-size: 18px;
  line-height: 1;
}
.mobile-reveal-btn small {
  display: block;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .02em;
  opacity: .95;
}
.trash-icon {
  width: 22px;
  height: 22px;
}
.task-main { display: flex; align-items: flex-start; gap: 14px; flex: 1; min-width: 0; }
.drag-handle { width: 28px; height: 30px; min-width: 28px; border: none; background: transparent; color: #94a3b8; cursor: grab; font-size: 18px; line-height: 1; padding: 0; margin-top: 1px; transition: transform .16s ease, color .16s ease; -webkit-appearance: none; appearance: none; }
.task-row.dragging .drag-handle,
.task-row.touch-reordering .drag-handle { cursor: grabbing; color: #64748b; transform: scale(1.08); }
.drag-handle:disabled,
.mobile-reveal-btn:disabled { opacity: .35; cursor: default; }
.toggle-btn { width: 32px; height: 32px; min-width: 32px; min-height: 32px; flex: 0 0 32px; padding: 0; border-radius: 999px; border: 2px solid rgba(148, 163, 184, 0.55); background: rgba(255, 255, 255, 0.92); cursor: pointer; position: relative; display: inline-grid; place-items: center; line-height: 0; aspect-ratio: 1 / 1; -webkit-appearance: none; appearance: none; transition: transform 0.18s ease, border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease; }
.toggle-btn:hover { transform: translateY(-1px); }
.done .toggle-btn { border-color: rgba(34, 197, 94, 0.72); background: rgba(34, 197, 94, 0.18); box-shadow: inset 0 0 0 1px rgba(34, 197, 94, 0.08); }
.check-mark { position: relative; display: block; width: 100%; height: 100%; }
.done .check-mark::before { content: ''; position: absolute; top: 7px; left: 10px; width: 7px; height: 12px; border-right: 2px solid #166534; border-bottom: 2px solid #166534; transform: rotate(40deg); }
.task-content { flex: 1; min-width: 0; display: grid; gap: 12px; }
.task-input { width: 100%; border: none; background: transparent; resize: none; overflow: hidden; color: #1f2937; font-size: 18px; line-height: 1.55; outline: none; padding: 2px 0; }
.done .task-input { text-decoration: line-through; color: #64748b; }
.task-image-card { display: grid; gap: 8px; max-width: 360px; padding: 10px; border-radius: 18px; background: rgba(255, 255, 255, 0.78); border: 1px solid rgba(148, 163, 184, 0.24); box-shadow: none; }
.task-image-link { display: block; }
.task-image { display: block; width: 100%; max-height: 260px; object-fit: cover; border-radius: 14px; }
.task-image-meta { display: flex; align-items: center; justify-content: space-between; gap: 12px; color: #475569; font-size: 13px; }
.task-actions { display: flex; align-items: center; gap: 6px; }
.underlay-spinner {
  width: 20px;
  height: 20px;
  display: inline-block;
  border-radius: 999px;
  border: 2px solid rgba(15,23,42,.16);
  border-top-color: rgba(15,23,42,.72);
  animation: task-spin .8s linear infinite;
}
.underlay-spinner.light {
  border-color: rgba(255,255,255,.28);
  border-top-color: #fff;
}
.file-input { display: none; }
.icon-btn { width: 38px; height: 38px; display: inline-grid; place-items: center; border: 1px solid rgba(148, 163, 184, 0.26); background: rgba(255, 255, 255, 0.88); color: #334155; border-radius: 999px; font-size: 18px; font-weight: 800; cursor: pointer; -webkit-appearance: none; appearance: none; }
.icon-btn.danger { color: #9f1239; }
.icon-btn.subtle { color: #475569; }
.upload-btn.disabled,
.icon-btn:disabled { opacity: 0.55; cursor: not-allowed; }

@keyframes task-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 820px) {
  .task-row.mobile { padding: 7.5px 0; overflow: visible; }
  .task-row.mobile .task-card {
    padding-left: 10px;
    padding-right: 10px;
    border-radius: 20px;
    box-shadow: none;
    backdrop-filter: blur(8px);
  }
  .task-row.mobile.touch-reordering .task-card {
    opacity: 0.4;
    background: rgba(255,255,255,.195);
    border-radius: 22px;
    box-shadow: 0 28px 56px rgba(15,23,42,.22), 0 14px 24px rgba(15,23,42,.12);
  }
  .task-row.mobile .task-actions { gap: 6px; }
  .task-row.mobile .task-actions .delete-btn { display: none; }
  .task-row.mobile .task-input { touch-action: pan-y; }
}

@media (min-width: 821px) {
  .swipe-underlay { display: none; }
  .task-actions button {
    margin-left: 6px;
  }
}
</style>
